import { WorkSamples } from './WorkSamples';
import { BookOpen, CalendarClock, ExternalLink, Link2, NotebookPen, RefreshCw, Repeat, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../lib/api';
import type { Child, JournalEntry, LessonPlan, PlannerItem } from '../lib/types';
import { PlannerCalendar, timeRange, ymd, type CalendarItem } from '../ui/PlannerCalendar';
import { JournalRow, JournalSheet, journalPreview } from './Journal';
import { LessonPlanForm, LessonPlanRow, LessonPlanSheet, lessonTitle } from './LessonPlans';
import { Sheet } from '../ui/Sheet';
import { SubjectField } from '../ui/SubjectField';
import { CategorySelect, PORTFOLIO, PortfolioWork } from './GradeBook';
import { useParent } from './ParentContext';
import { childHas } from './Plans';
import { ConnexusSettings, SchoolBanner, useSchoolLink } from './SchoolLink';

const linkHref = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/** Planner feed providers (SCCA #167). The iCal engine is the same for all — this is just the label and the
 *  "where do I find my Sync link?" help. `key` must match the server's PLANNER_PROVIDERS. */
const PLANNER_PROVIDERS: { key: string; label: string; help: string; placeholder: string }[] = [
  { key: 'connexus', label: 'Connexus', help: 'In Connexus / Pearson Online Classroom: Main Menu → Calendar → Sync, then copy the iCal address.', placeholder: 'https://…/calendar.ics' },
  { key: 'google', label: 'Google Calendar', help: 'Google Calendar → Settings → the calendar → “Secret address in iCal format”.', placeholder: 'https://calendar.google.com/calendar/ical/…/basic.ics' },
  { key: 'canvas', label: 'Canvas', help: 'Canvas → Calendar → “Calendar Feed” (bottom-right), then copy the link.', placeholder: 'https://…/feeds/calendars/….ics' },
  { key: 'apple', label: 'Apple / iCloud', help: 'In Calendar, share the calendar as Public, then copy its webcal:// link.', placeholder: 'webcal://…-caldav.icloud.com/published/…' },
  { key: 'outlook', label: 'Outlook', help: 'Outlook → Settings → Calendar → Shared calendars → Publish, then copy the ICS link.', placeholder: 'https://outlook.office365.com/…/calendar.ics' },
  { key: 'other', label: 'Other (iCal / webcal)', help: 'Paste any iCalendar (.ics) or webcal:// subscription link.', placeholder: 'https://…/calendar.ics' },
];
const providerLabel = (key: string) => PLANNER_PROVIDERS.find((p) => p.key === key)?.label ?? 'Imported';

/** What the calendar shows: assignments, lesson plans (#186) and journal entries (#187), each rendered its own way. */
type Entry = CalendarItem &
  ({ kind: 'assignment'; item: PlannerItem } | { kind: 'lesson'; plan: LessonPlan } | { kind: 'journal'; entry: JournalEntry });

/** The Planner tab: a month calendar of assignments for a child, with check-off (#3, #126). */
export function Planner({ child }: { child: Child }) {
  const { toast } = useParent();
  const [items, setItems] = useState<PlannerItem[] | null>(null);
  const [plans, setPlans] = useState<LessonPlan[]>([]);
  const [adding, setAdding] = useState<string | null>(null); // the due date for a new item, or null
  const [addingLesson, setAddingLesson] = useState<string | null>(null);
  const [openLesson, setOpenLesson] = useState<LessonPlan | null>(null);
  const [journal, setJournal] = useState<JournalEntry[]>([]);
  const [openJournal, setOpenJournal] = useState<string | null>(null); // the day being read/written
  // A link to one assignment (`?item=<id>`, from the Family page's due and overdue cards, #358) opens it here.
  const [params, setParams] = useSearchParams();
  const linkedId = params.get('item');
  const closeLinked = () =>
    setParams(
      (p) => {
        p.delete('item');
        return p;
      },
      { replace: true },
    );

  const load = useCallback(() => {
    api.get<PlannerItem[]>(`/api/children/${child.id}/planner`).then(setItems).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
    api.get<LessonPlan[]>(`/api/children/${child.id}/lesson-plans`).then(setPlans).catch(() => undefined);
    api.get<JournalEntry[]>(`/api/children/${child.id}/journal`).then(setJournal).catch(() => undefined);
  }, [child.id, toast]);
  useEffect(load, [load]);

  // External planner feed — a universal iCal integration (SCCA #167): Connexus, Google Calendar, Canvas, etc.
  const [linked, setLinked] = useState(!!child.plannerLinked);
  const [source, setSource] = useState(child.plannerSource || '');
  const [linkOpen, setLinkOpen] = useState(false);
  const [provider, setProvider] = useState(child.plannerSource || 'connexus');
  const [feedUrl, setFeedUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const providerInfo = PLANNER_PROVIDERS.find((p) => p.key === provider) ?? PLANNER_PROVIDERS[0]!;
  // Connexus read by the browser extension (#654): its new Planner has no calendar link.
  const { info: school, setInfo: setSchool } = useSchoolLink(child.id);
  const [schoolOpen, setSchoolOpen] = useState(false);
  const connexusReader = provider === 'connexus' && !!school?.available;

  const linkFeed = async () => {
    setBusy(true);
    try {
      const r = await api.put<{ count: number }>(`/api/children/${child.id}/planner-source`, { url: feedUrl.trim(), source: provider });
      setLinked(true);
      setSource(provider);
      setLinkOpen(false);
      setFeedUrl('');
      toast({ text: `Linked ${providerLabel(provider)} — imported ${r.count} item${r.count === 1 ? '' : 's'}.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  };
  const syncFeed = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ count: number }>(`/api/children/${child.id}/planner/sync`);
      toast({ text: `Synced ${r.count} item${r.count === 1 ? '' : 's'} from ${providerLabel(source)}.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  };
  const unlinkFeed = async () => {
    if (!confirm(`Unlink the ${providerLabel(source)} planner? Imported items will be removed.`)) return;
    setBusy(true);
    try {
      await api.put(`/api/children/${child.id}/planner-source`, { url: '' });
      setLinked(false);
      setSource('');
      toast({ text: 'Planner unlinked.', tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <p className="muted">Loading…</p>;

  const undated = items.filter((it) => !it.dueDate);
  const linkedItem = linkedId ? items.find((it) => it.id === linkedId) : undefined;
  const linkedOverdue = !!linkedItem && !linkedItem.done && !!linkedItem.dueDate && linkedItem.dueDate < ymd(new Date());

  return (
    <section className="card stack">
      <div className="row-between">
        <h2 className="row-gap">
          <NotebookPen size={18} /> {child.name}’s planner
        </h2>
        <span className="row-gap">
          {linked ? (
            <>
              <button className="btn small" onClick={syncFeed} disabled={busy} title={`Re-import from ${providerLabel(source)}`}>
                <RefreshCw size={14} /> Sync {providerLabel(source)}
              </button>
              <button className="btn small danger-text" onClick={unlinkFeed} disabled={busy}>
                Unlink
              </button>
            </>
          ) : (
            <button className="btn small" onClick={() => setLinkOpen((v) => !v)} title="Import lessons from a calendar feed">
              <Link2 size={14} /> Link a planner
            </button>
          )}
        </span>
      </div>

      {linkOpen && !linked && (
        <div className="stack sub-section">
          <div className="row-gap">
            <label>
              Planner
              <select value={provider} onChange={(e) => setProvider(e.target.value)}>
                {PLANNER_PROVIDERS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {connexusReader && school ? (
            <ConnexusSettings
              child={child}
              info={school}
              onSaved={(i) => {
                setSchool(i);
                load();
              }}
              onClose={() => setLinkOpen(false)}
            />
          ) : (
            <>
              <p className="muted small">
                {providerInfo.help} {child.name}’s items will show up in this planner and re-sync automatically.
              </p>
              <div className="row-gap">
                <input className="grow" placeholder={providerInfo.placeholder} value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} />
                <button className="btn primary small" onClick={linkFeed} disabled={busy || !feedUrl.trim()}>
                  Link &amp; sync
                </button>
                <button className="icon-btn" onClick={() => setLinkOpen(false)} aria-label="Cancel">
                  <X size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <SchoolBanner
        child={child}
        info={school}
        part="planner"
        onSettings={() => setSchoolOpen((v) => !v)}
        onUpdated={(i) => {
          setSchool(i);
          load();
        }}
      />
      {schoolOpen && school && (
        <ConnexusSettings
          child={child}
          info={school}
          onSaved={(i) => {
            setSchool(i);
            load();
          }}
          onClose={() => setSchoolOpen(false)}
        />
      )}

      <PlannerCalendar<Entry>
        items={[
          ...journal.map((j): Entry => ({ id: `journal:${j.date}`, title: journalPreview(j.bodyHtml, 40) || 'Journal', dueDate: j.date, done: false, kind: 'journal', entry: j })),
          ...plans.map((p): Entry => ({ id: `lesson:${p.id}`, title: lessonTitle(p), dueDate: p.date, done: false, kind: 'lesson', plan: p })),
          ...items.map((it): Entry => ({ id: it.id, title: it.title, dueDate: it.dueDate, done: it.done, kind: 'assignment', item: it, startTime: it.startTime, endTime: it.endTime })),
        ]}
        renderItem={(e, { closePopup }) =>
          e.kind === 'journal' ? (
            <JournalRow
              entry={e.entry}
              onOpen={() => {
                closePopup();
                setOpenJournal(e.entry.date);
              }}
            />
          ) : e.kind === 'lesson' ? (
            <LessonPlanRow
              plan={e.plan}
              onOpen={() => {
                closePopup();
                setOpenLesson(e.plan);
              }}
              onChanged={load}
            />
          ) : (
            <PlannerRow
              child={child}
              item={e.item}
              reload={load}
              plans={plans}
              onOpenLesson={(p) => {
                closePopup();
                setOpenLesson(p);
              }}
            />
          )
        }
        actions={[
          { label: 'Assignment', onAdd: (date) => setAdding(date) },
          ...(childHas(child, 'lesson_plans') ? [{ label: 'Lesson plan', onAdd: (date: string) => setAddingLesson(date) }] : []),
          ...(childHas(child, 'journal') ? [{ label: 'Journal', onAdd: (date: string) => setOpenJournal(date) }] : []),
        ]}
        storageKey="cg.planner.view.parent"
        focusDate={linkedItem?.dueDate || undefined}
      />

      {linkedId && (
        <Sheet title={linkedOverdue ? 'Overdue assignment' : 'Assignment'} onClose={closeLinked}>
          {linkedItem ? (
            <div className="stack">
              <PlannerRow
                child={child}
                item={linkedItem}
                reload={load}
                plans={plans}
                onOpenLesson={(p) => {
                  closeLinked();
                  setOpenLesson(p);
                }}
              />
              <WorkSamples child={child} plannerItemId={linkedItem.id} />
            </div>
          ) : (
            <p className="muted">That assignment isn’t in {child.name}’s planner anymore.</p>
          )}
        </Sheet>
      )}

      {undated.length > 0 && (
        <div className="stack sub-section">
          <h3>Unscheduled</h3>
          {undated.map((it) => (
            <PlannerRow key={it.id} child={child} item={it} reload={load} plans={plans} onOpenLesson={setOpenLesson} />
          ))}
        </div>
      )}

      {addingLesson !== null && (
        <LessonPlanForm
          childId={child.id}
          date={addingLesson}
          onClose={() => setAddingLesson(null)}
          onSaved={() => {
            setAddingLesson(null);
            load();
          }}
        />
      )}
      {openJournal && (
        <JournalSheet
          key={openJournal}
          childId={child.id}
          childName={child.name}
          date={openJournal}
          entry={journal.find((j) => j.date === openJournal)}
          onClose={() => setOpenJournal(null)}
          onChanged={load}
        />
      )}
      {openLesson && <LessonPlanSheet plan={openLesson} onClose={() => setOpenLesson(null)} onChanged={load} />}

      {adding !== null && (
        <Sheet title="New assignment" onClose={() => setAdding(null)}>
          <PlannerForm
            child={child}
            childId={child.id}
            initialDate={adding}
            plans={plans}
            bare
            onDone={() => {
              setAdding(null);
              load();
            }}
            onCancel={() => setAdding(null)}
          />
        </Sheet>
      )}
    </section>
  );
}

function PlannerRow({
  child,
  item,
  reload,
  plans,
  onOpenLesson,
}: {
  child: Child;
  item: PlannerItem;
  reload: () => void;
  plans: LessonPlan[];
  onOpenLesson: (p: LessonPlan) => void;
}) {
  const { toast } = useParent();
  const [editing, setEditing] = useState(false);
  const [askSeries, setAskSeries] = useState(false);
  const lesson = item.lessonPlanId ? plans.find((p) => p.id === item.lessonPlanId) : undefined;

  async function toggleDone() {
    await api.patch(`/api/planner/${item.id}`, { done: !item.done }).then(reload).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }
  async function remove(series = false) {
    // A repeating assignment (#324) asks whether to delete just this one or the rest of the series too.
    if (item.seriesId && !series && !askSeries) return setAskSeries(true);
    if (!item.seriesId && !confirm(`Delete “${item.title}”?`)) return;
    setAskSeries(false);
    await api
      .del(`/api/planner/${item.id}${series ? '?series=1' : ''}`)
      .then(reload)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }

  if (editing) return <PlannerForm child={child} item={item} plans={plans} onDone={() => { setEditing(false); reload(); }} onCancel={() => setEditing(false)} />;

  return (
    <div className={`planner-item ${item.done ? 'done' : ''}`}>
      <label className="check planner-check">
        <input type="checkbox" checked={item.done} onChange={toggleDone} />
        <span className="grow">
          <strong className="planner-title">
            {item.title}
            {item.source && (
              <span className="tag" style={{ marginLeft: 6 }} title={`Imported from your ${providerLabel(item.source)} planner`}>
                {providerLabel(item.source)}
              </span>
            )}
            {item.category === PORTFOLIO && (
              <span className="tag portfolio-tag" style={{ marginLeft: 6 }} title="Its work goes in the portfolio">
                Portfolio
              </span>
            )}
            {item.isGraded && (
              <span className="tag" style={{ marginLeft: 6 }} title="In the grade book">
                Graded
              </span>
            )}
            {item.submittedAt && (
              <span className="tag turned-in" style={{ marginLeft: 6 }} title="Turned in from the Student Portal">
                Turned in {item.sticker}
              </span>
            )}
            {item.seriesId && (
              <span className="tag" style={{ marginLeft: 6 }} title="One of a repeating assignment">
                <Repeat size={11} /> Repeats
              </span>
            )}
          </strong>
          {(item.subject || item.category || item.startTime) && (
            <span className="muted small block">
              <CalendarClock size={12} />{' '}
              {[item.startTime ? timeRange(item.startTime, item.endTime) : '', item.subject, item.category].filter(Boolean).join(' · ')}
            </span>
          )}
          {item.description && <span className="planner-desc block">{item.description}</span>}
          {item.submittedAt && item.studentNote && <span className="planner-learned block">What they learned: “{item.studentNote}”</span>}
          {item.url && (
            <a className="planner-link small" href={linkHref(item.url)} target="_blank" rel="noreferrer">
              <ExternalLink size={12} /> Open link
            </a>
          )}
          {lesson && (
            <button
              type="button"
              className="planner-link small link-btn"
              onClick={(e) => {
                e.preventDefault();
                onOpenLesson(lesson);
              }}
            >
              <BookOpen size={12} /> Lesson plan: {lessonTitle(lesson)} ({shortDay(lesson.date)})
            </button>
          )}
        </span>
      </label>
      {!item.source && (
        <span className="row-gap planner-actions">
          <button className="btn small" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn small danger-text" onClick={() => remove()} title="Delete">
            <Trash2 size={14} />
          </button>
        </span>
      )}
      {askSeries && (
        <div className="series-delete row-gap">
          <span className="small">Delete this repeating assignment:</span>
          <button className="btn small danger-text" onClick={() => remove()}>
            Just this one
          </button>
          <button className="btn small danger-text" onClick={() => remove(true)}>
            This and the rest
          </button>
          <button className="btn small" onClick={() => setAskSeries(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** '2026-10-12' -> 'Oct 12'. */
const shortDay = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function PlannerForm({
  child,
  childId,
  item,
  initialDate,
  plans = [],
  bare,
  onDone,
  onCancel,
}: {
  /** For a Portfolio-type assignment's work (#637). */
  child?: Child;
  childId?: string;
  item?: PlannerItem;
  initialDate?: string;
  /** The child's lesson plans, to tie the assignment to one (#320). */
  plans?: LessonPlan[];
  /** Rendered inside a Sheet, which already has the title and close button. */
  bare?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { toast } = useParent();
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [subject, setSubject] = useState(item?.subject ?? '');
  const [category, setCategory] = useState(item?.category ?? '');
  const [url, setUrl] = useState(item?.url ?? '');
  const [dueDate, setDueDate] = useState(item?.dueDate ?? initialDate ?? '');
  const [isGraded, setIsGraded] = useState(!!item?.isGraded);
  const [lessonPlanId, setLessonPlanId] = useState(item?.lessonPlanId ?? '');
  // An optional time block on the due date (#366), like a live lesson.
  const [startTime, setStartTime] = useState(item?.startTime ?? '');
  const [endTime, setEndTime] = useState(item?.endTime ?? '');
  // Repeat (#324), only when adding: '' (once), daily (school days) or weekly, until a date.
  const [repeat, setRepeat] = useState<'' | 'daily' | 'weekly'>('');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      // Un-marking a graded item takes it (and any score) out of the grade book (#184), so check first.
      if (item?.isGraded && !isGraded && !confirm('Turn off “Is graded”? This removes the assignment — and any score — from the grade book.')) {
        setBusy(false);
        return;
      }
      const payload = {
        title: title.trim(),
        description,
        subject: subject.trim(),
        category,
        url: url.trim(),
        dueDate,
        isGraded,
        lessonPlanId,
        startTime: dueDate ? startTime : '',
        endTime: dueDate && startTime ? endTime : '',
      };
      if (item) await api.patch(`/api/planner/${item.id}`, payload);
      else {
        const r = await api.post<{ created?: number }>(`/api/children/${childId}/planner`, repeat ? { ...payload, repeat: { every: repeat, until } } : payload);
        if (repeat) toast({ text: `Added ${r.created ?? 0} assignments.`, tone: 'good' });
      }
      onDone();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={bare ? 'stack' : 'card stack assignment-form'}>
      {!bare && (
        <div className="row-between">
          <strong>{item ? 'Edit assignment' : 'New assignment'}</strong>
          <button className="icon-btn" onClick={onCancel} aria-label="Cancel">
            <X size={16} />
          </button>
        </div>
      )}
      <label>
        Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="e.g. Read chapter 4" />
      </label>
      <label>
        Description
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={3} placeholder="Notes, instructions…" />
      </label>
      <div className="field-row">
        <label>
          Subject
          <SubjectField value={subject} onChange={setSubject} placeholder="e.g. English" />
        </label>
        <label>
          Category
          <CategorySelect value={category} onChange={setCategory} />
        </label>
        <label>
          Due date
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>
      </div>
      {dueDate && (
        <div className="field-row">
          <label>
            Starts at <span className="muted small">(optional)</span>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} aria-label="Starts at" />
          </label>
          <label>
            Ends at <span className="muted small">(optional)</span>
            <input type="time" value={endTime} min={startTime || undefined} onChange={(e) => setEndTime(e.target.value)} disabled={!startTime} aria-label="Ends at" />
          </label>
        </div>
      )}
      {dueDate && !startTime && <p className="muted small">Leave the time empty if it can be done any time that day. Add one for things like a live lesson.</p>}
      {category === PORTFOLIO && child && <PortfolioWork child={child} plannerItemId={item?.id} />}
      {!item && (
        <div className="field-row">
          <label>
            Repeat
            <select value={repeat} onChange={(e) => setRepeat(e.target.value as '' | 'daily' | 'weekly')}>
              <option value="">Doesn’t repeat</option>
              <option value="daily">Every school day</option>
              <option value="weekly">Every week</option>
            </select>
          </label>
          {repeat && (
            <label>
              Until
              <input type="date" value={until} min={dueDate || undefined} onChange={(e) => setUntil(e.target.value)} />
            </label>
          )}
        </div>
      )}
      {repeat && !item && (
        <p className="muted small">
          Starts on the due date{repeat === 'daily' ? ' and goes on every school day' : ' and repeats on that weekday'}, skipping holidays and breaks.
        </p>
      )}
      <LessonPicker plans={plans} subject={subject} value={lessonPlanId} onChange={setLessonPlanId} />
      <label>
        Link <span className="muted small">(optional)</span>
        <input value={url} onChange={(e) => setUrl(e.target.value)} maxLength={500} placeholder="https://…" />
      </label>
      <label className="check">
        <input type="checkbox" checked={isGraded} onChange={(e) => setIsGraded(e.target.checked)} />
        <span>
          Is graded
          <span className="muted small block">Adds it to the grade book, where you enter the score once it’s marked.</span>
        </span>
      </label>
      <button className="btn primary" onClick={submit} disabled={busy || !title.trim() || (!!repeat && (!dueDate || !until))}>
        {item ? 'Save' : 'Add'}
      </button>
    </div>
  );
}

/**
 * Tie an assignment to a lesson plan (#320): the child's plans for the chosen subject (all of them without one),
 * narrowed by topic and date.
 */
function LessonPicker({ plans, subject, value, onChange }: { plans: LessonPlan[]; subject: string; value: string; onChange: (id: string) => void }) {
  const [topic, setTopic] = useState('');
  const [date, setDate] = useState('');
  const matches = useMemo(() => {
    const s = subject.trim().toLowerCase();
    const q = topic.trim().toLowerCase();
    return plans
      .filter((p) => !s || p.subject.toLowerCase() === s)
      .filter((p) => !q || p.topic.toLowerCase().includes(q))
      .filter((p) => !date || p.date === date)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [plans, subject, topic, date]);
  const chosen = plans.find((p) => p.id === value);
  if (!plans.length) return null;
  const options = chosen && !matches.includes(chosen) ? [chosen, ...matches] : matches;
  return (
    <fieldset className="lesson-picker stack">
      <legend>
        Lesson plan <span className="muted small">(optional{subject.trim() ? `, ${subject.trim()} plans` : ''})</span>
      </legend>
      <div className="field-row">
        <label>
          Topic
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Filter by topic" />
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Lesson plan">
        <option value="">No lesson plan</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {shortDay(p.date)} · {lessonTitle(p)}
          </option>
        ))}
      </select>
      {!matches.length && <span className="muted small">No lesson plans match{subject.trim() ? ` for ${subject.trim()}` : ''}.</span>}
    </fieldset>
  );
}
